/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import clsx from 'clsx'
import { CSSProperties } from 'react'
import { Icon } from '../../utils/Icon'
import { Horizon } from '../map_facilities/Horizon'
import { ButtonLink } from './ButtonLink'
import { CardSection, Heading } from './CardSection'
import { ChildIcon } from './ChildIcon'
import { VStack } from './VStack'

export type ChildAvailable = {
  name: string
  imageUrl?: string | null
  status: 'available' | 'interview_scheduled'
}

export type ChildrenAvailableProps = {
  childList: ChildAvailable[]
  reservationPageUrl?: string
}

export const ChildrenAvailable = ({
  childList,
  reservationPageUrl = '#',
}: ChildrenAvailableProps) => {
  return (
    <CardSection css={childrenAvailableStyle}>
      <Heading>ご利用可能な子ども</Heading>
      <VStack gap="10px">
        {childList.map((child) => {
          const iconName =
            child.status === 'available' ? 'complete_green' : 'users'
          return (
            <Horizon columnGaps={[5, 10, 5]} key={child.name}>
              <ChildIcon size={22} imageUrl={child.imageUrl} />
              <span>{child.name}</span>
              <Icon iconName={iconName} style={iconStyle} />
              <span
                className={clsx(
                  'status',
                  child.status === 'available' && 'completed',
                )}
              >
                {child.status === 'available' ? '利用可能' : '面談予約済'}
              </span>
            </Horizon>
          )
        })}
      </VStack>
      {childList.length > 0 ? (
        <ButtonLink
          style={{
            marginTop: '25px',
          }}
          href={reservationPageUrl}
        >
          予約する
        </ButtonLink>
      ) : (
        <p style={{ textAlign: 'center' }}>
          まずは、施設に子どもを登録してください。
        </p>
      )}
    </CardSection>
  )
}

const childrenAvailableStyle = css`
  .status {
    font-weight: bold;
    font-size: 14px;
    line-height: 150%;

    &.completed {
      color: #0cb200;
    }
  }
`

export type ChildUnregistered = {
  name: string
  imageUrl?: string | null
}

export type ChildrenBeforeRegistrationProps = {
  childList: ChildUnregistered[]
  usagePageUrl: string
  registrationPageUrl?: string
}
export const ChildrenBeforeRegistration = ({
  childList,
  usagePageUrl,
  registrationPageUrl = '#',
}: ChildrenBeforeRegistrationProps) => {
  return (
    <CardSection css={childrenBeforeAcceptanceStyle}>
      <Heading>この施設に登録されていない子ども</Heading>
      <VStack gap="10px">
        {childList.map((child) => (
          <Horizon columnGaps={[5, 10, 5]} key={child.name}>
            <ChildIcon imageUrl={child.imageUrl} size={22} />
            <span>{child.name}</span>
            <Icon iconName="caution_red" style={iconStyle} />
            <span className="status">要登録</span>
          </Horizon>
        ))}
      </VStack>
      <ButtonLink
        style={{
          marginTop: '25px',
        }}
        href={registrationPageUrl}
      >
        施設へ登録する
      </ButtonLink>
      <Horizon
        columnGap="2px"
        justifyContent="center"
        style={{ marginTop: '25px' }}
      >
        <Icon
          iconName="help_black"
          style={{
            height: '14px',
            width: '14px',
          }}
        />
        <a href={usagePageUrl} className="link">
          ご利用可能になるまでの流れ
        </a>
      </Horizon>
    </CardSection>
  )
}

const childrenBeforeAcceptanceStyle = css`
  .status {
    font-weight: bold;
    font-size: 14px;
    line-height: 150%;
    color: #ae000e;
  }

  .link {
    font-size: 12px;
    line-height: 150%;
    text-align: center;
    text-decoration-line: underline;

    &:visited,
    &:link {
      color: inherit;
    }
  }
`

const iconStyle: CSSProperties = {
  height: '18px',
  width: '18px',
}
